/* eslint-disable no-case-declarations */
import React, {useCallback, type FC} from 'react';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {useGlobals, withGlobals} from '../../../../globalPropsContext';
import s from './FiltersList.scss';
import {classes as stylableClasses} from './FiltersList.st.css';
import classNames from 'classnames';
import {ConditionalRender} from '../../../../common/components/ConditionalRender/ConditionalRender';
import type {FilterModel, IFilterModel} from '../../../../types/galleryTypes';
import {FilterType} from '../../../../types/galleryTypes';
import {PriceFilterModel} from '../../../../models/PriceFilterModel';
import {ExpandableOption} from '../../ExpandableOption/ExpandableOption';
import {RadioButton, RadioButtonGroup} from 'wix-ui-tpa/cssVars';
import {hasActiveOptions} from '../filterUtils';
import {Experiments} from '../../../../constants';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {ColorFilter} from '../FilterComponents/ColorFilter/ColorFilter';
import {MultipleSelectionFilter} from '../FilterComponents/MultipleSelectionFilter/MultipleSelectionFilter';
import {RangeFilter} from '../FilterComponents/RangeFilter/RangeFilter';
import {RangeFilterClass} from '../FilterComponents/RangeFilter/RangeFilterClass';
import {SingleSelectionFilter} from '../FilterComponents/SingleSelectionFilter/SingleSelectionFilter';

export const FiltersListDataHooks = {
  List: 'filters-list',
  SortInMobilePanel: 'sort-in-mobile-panel',
  FilterTypeSort: 'filter-type-sort',
  FilterType: (filterType: FilterType) => `filter-type-${filterType}`,
} as const;

export interface IFiltersListProps extends IProvidedTranslationProps {
  combineFiltersAndSort?: boolean;
}

const FiltersListComp: FC<IFiltersListProps> = ({t, combineFiltersAndSort}) => {
  const {
    filterModels,
    shouldShowMobile,
    isCategoryPage,
    usingExtendedGridGalleryStyles,
    hasActiveSortOption,
    selectedSort,
    sortingOptions,
    sortProducts,
    hasFilters,
    styles,
    stylesParams,
  } = useGlobals();

  const {experiments} = useExperiments();
  const rangeFilterRefactor = experiments.enabled(Experiments.RangeFilterRefactor);
  const galleryPriceSliderA11yFixKeyboardFocus = experiments.enabled(
    Experiments.GalleryPriceSliderA11yFixKeyboardFocus
  );

  const shouldRenderSort = combineFiltersAndSort && hasActiveSortOption;

  const getMobileSortingComponent = useCallback(() => {
    const sortingOptionsComp = (
      <RadioButtonGroup
        className={stylableClasses.radioButtonGroupRoot}
        data-hook={FiltersListDataHooks.SortInMobilePanel}
        value={selectedSort.id}
        name="sort-by"
        onChange={(optionId) => {
          sortProducts(sortingOptions.find((option) => option.id === optionId));
        }}>
        {sortingOptions.map((option) => (
          <RadioButton
            className={stylableClasses.radioButtonRoot}
            label={t(option.titleKey)}
            value={option.id}
            key={option.id}
          />
        ))}
      </RadioButtonGroup>
    );

    if (!hasFilters) {
      return (
        <li key="sort" data-hook={FiltersListDataHooks.FilterTypeSort} className={s.filterListItem}>
          {sortingOptionsComp}
        </li>
      );
    }

    return (
      <>
        <ConditionalRender by="shouldRenderSortOptionsWhenFiltersDisabled">
          <li key="sort" data-hook={FiltersListDataHooks.FilterTypeSort} className={s.filterListItem}>
            {sortingOptionsComp}
          </li>
        </ConditionalRender>
        <ConditionalRender by="galleryShowFilters">
          <li key="sort" data-hook={FiltersListDataHooks.FilterTypeSort} className={s.filterListItem}>
            <ExpandableOption title={t('sortByText')} expanded={true}>
              {sortingOptionsComp}
            </ExpandableOption>
          </li>
        </ConditionalRender>
      </>
    );
  }, [hasFilters, t, selectedSort, sortProducts, sortingOptions]);

  const filterTitle = useCallback(
    (filterModel: FilterModel) => {
      let title = filterModel.title;

      if (!shouldShowMobile) {
        return title;
      }

      if (Array.isArray(filterModel.activeOptions)) {
        const length = filterModel.activeOptions.length;
        if (length > 0) {
          title += ` (${length})`;
        }
      }

      if (filterModel.activeOptions && (filterModel as PriceFilterModel).activeOptions.minPrice) {
        const filterModelAsPrice = filterModel as PriceFilterModel;
        const minPriceFormatted = filterModelAsPrice.options.find(
          (v) => v.key === filterModelAsPrice.activeOptions.minPrice
        ).value;
        const maxPriceFormatted = filterModelAsPrice.options.find(
          (v) => v.key === filterModelAsPrice.activeOptions.maxPrice
        ).value;
        title += ` (${minPriceFormatted} - ${maxPriceFormatted})`;
      }

      return title;
    },
    [shouldShowMobile]
  );

  const isExpanded = useCallback(
    (index: number, filterModel: IFilterModel) => {
      // This is css per breakpoint safe because this code runs only after a user click and will never be reached in SSR
      const isSortSettingEnabled = styles.get(stylesParams.galleryShowSort);
      const isSortEnabled = shouldRenderSort && isSortSettingEnabled;
      const isFirstFilter = !isSortEnabled && index === 0;
      return isFirstFilter || hasActiveOptions(filterModel);
    },
    [shouldRenderSort, styles, stylesParams]
  );

  const getFilterComponent = useCallback(
    (filterModel: FilterModel) => {
      switch (filterModel.filterType) {
        case FilterType.COLLECTION:
          return usingExtendedGridGalleryStyles ? (
            <MultipleSelectionFilter
              filterId={filterModel.filterId}
              selected={[...filterModel.activeOptions]}
              options={filterModel.options.map((option) => ({
                id: option.key,
                name: option.value,
              }))}
            />
          ) : (
            <SingleSelectionFilter
              selected={filterModel.activeOptions}
              filterId={filterModel.filterId}
              options={filterModel.options.map((option) => ({
                id: option.key,
                name: option.value,
              }))}
            />
          );
        case FilterType.CUSTOM_COLLECTION:
          return (
            <MultipleSelectionFilter
              filterId={filterModel.filterId}
              selected={[...filterModel.activeOptions]}
              options={filterModel.options.map((option) => ({
                id: option.key,
                name: option.value,
              }))}
            />
          );
        case FilterType.COLOR_OPTION:
          return (
            <ColorFilter
              selected={[...filterModel.activeOptions]}
              filterId={filterModel.filterId}
              colorOptions={filterModel.options.map((option) => ({key: option.key, value: option.value}))}
            />
          );
        case FilterType.LIST_OPTION:
          return (
            <MultipleSelectionFilter
              selected={[...filterModel.activeOptions, ...filterModel.selectedOptions]}
              filterId={filterModel.filterId}
              options={filterModel.options.map((option) => ({
                id: option.key,
                name: option.value,
              }))}
            />
          );
        case FilterType.PRICE:
          const selectedRange = {
            min: filterModel.activeOptions.minPrice,
            max: filterModel.activeOptions.maxPrice,
          };

          return rangeFilterRefactor ? (
            <RangeFilter
              filterId={filterModel.filterId}
              options={filterModel.options}
              selectedRange={selectedRange}
              key={galleryPriceSliderA11yFixKeyboardFocus ? '' : `${selectedRange.min}-${selectedRange.max}`}
            />
          ) : (
            <RangeFilterClass
              filterId={filterModel.filterId}
              options={filterModel.options}
              selectedRange={selectedRange}
              key={galleryPriceSliderA11yFixKeyboardFocus ? '' : `${selectedRange.min}-${selectedRange.max}`}
            />
          );
      }
    },
    [rangeFilterRefactor, galleryPriceSliderA11yFixKeyboardFocus, usingExtendedGridGalleryStyles]
  );

  return (
    <ul
      data-hook={FiltersListDataHooks.List}
      className={classNames(s.filterList, {
        [s.categoryPage]: isCategoryPage,
        [s.usingExtendedGridGalleryStyles]: usingExtendedGridGalleryStyles,
      })}>
      {shouldRenderSort && <ConditionalRender by="galleryShowSort">{getMobileSortingComponent()}</ConditionalRender>}
      {hasFilters && (
        <ConditionalRender by="galleryShowFilters">
          {filterModels.map((filterModel: FilterModel, index) => (
            <li
              key={filterModel.filterId}
              data-hook={FiltersListDataHooks.FilterType(filterModel.filterType)}
              className={s.filterListItem}>
              <ExpandableOption
                title={filterTitle(filterModel)}
                filterType={filterModel.filterType}
                expanded={isExpanded(index, filterModel)}>
                {getFilterComponent(filterModel)}
              </ExpandableOption>
            </li>
          ))}
        </ConditionalRender>
      )}
    </ul>
  );
};

export const FiltersList = withGlobals(withTranslations()(FiltersListComp));
